import React, { useState } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { t } from 'i18next';
import { ArrowLeft, Mail } from 'lucide-react';
import { RadixButton, RadixCard, RadixInput, RadixSpinner } from '@/components/radix';
import { useLoginV3Flow } from './LoginV3FlowProvider';
import { loginV3LooksLikeEmail } from './loginV3UserIdentifier';

export default function LoginV3ResetEmailCard() {
    const { state, goBackInFlow, requestPasswordResetOtp, onResetEmailSuccess } = useLoginV3Flow();
    const [isPending, setIsPending] = useState(false);

    const formik = useFormik({
        enableReinitialize: true,
        initialValues: {
            email: (state.reset.email || (loginV3LooksLikeEmail(state.login.user) ? state.login.user : '')).trim(),
        },
        validationSchema: Yup.object({
            email: Yup.string().email(t('Common.PleaseEnterValidEmail')).required(t('Common.EmailRequired')),
        }),
        onSubmit: async (values) => {
            const success = await requestPasswordResetOtp({
                setter: setIsPending,
                email: values.email.trim(),
            });
            if (success) {
                onResetEmailSuccess(values.email.trim());
            }
            setIsPending(false);
        },
    });

    return (
        <RadixCard className="!p-0 border !border-[#E6E1D9] shadow-xl rounded-md bg-white/95 backdrop-blur-md overflow-hidden">
            <div className="relative bg-white p-6 sm:p-8 pb-4 text-center border-b border-[#F0EDE8]">
                <button
                    type="button"
                    onClick={goBackInFlow}
                    aria-label={t('Common.Back')}
                    className="absolute left-4 top-1/2 -translate-y-1/2 p-2 text-gray-400 hover:text-primary-500 transition-colors bg-transparent border-none outline-none cursor-pointer"
                >
                    <ArrowLeft size={20} />
                </button>

                <div className="w-14 h-14 bg-primary-500 text-white rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg rotate-3">
                    <Mail size={28} className="-rotate-3" />
                </div>

                <h1 className="text-xl sm:text-2xl font-bold text-gray-900 tracking-tight">{t('Common.LogForPass')}</h1>
                <p className="text-gray-500 text-[11px] sm:text-sm mt-1 font-medium italic">
                    {t('Common.LoginV3ResetEmailSubtitle')}
                </p>
            </div>

            <div className="p-6 sm:p-8">
                <form onSubmit={formik.handleSubmit} className="space-y-6">
                    <RadixInput
                        id="reset-email"
                        label={t('Common.Email')}
                        type="email"
                        autoComplete="email"
                        placeholder="name@company.com"
                        value={formik.values.email}
                        onChange={(e) => formik.setFieldValue('email', e.target.value)}
                        error={formik.touched.email ? formik.errors.email : undefined}
                        className="!rounded-xl !bg-white !h-12 sm:!h-14 !border-[#E6E1D9] focus:!border-primary-500"
                    />

                    <RadixButton
                        type="submit"
                        variant="primary"
                        className="w-full !h-12 sm:!h-14 !rounded-xl !text-base font-bold shadow-lg shadow-primary-500/10 text-white"
                        disabled={isPending}
                    >
                        {isPending ? <RadixSpinner size="sm" /> : t('Common.LogNext')}
                    </RadixButton>
                </form>
            </div>
        </RadixCard>
    );
}
